/** Mail is displayed/spoken locally, never returned to the action-capable model. */
export function privateMailToolOutput() {
  return JSON.stringify({
    answer: "The email result was presented privately. Wait for the user's next request. For a mail selection or continuation, use ui_icloud_mail again.",
    result: { specialist_id: "ui_icloud_mail", private_result_presented: true },
  });
}

export function privateMailSpeechChunks(text, maxChars = 320) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (!clean) return [];

  const limit = Math.max(120, Number(maxChars) || 320);
  const sentences = clean.match(/[^.!?]+[.!?]+(?:["'’”)]*)|[^.!?]+$/g) || [clean];
  const chunks = [];
  let current = "";

  const flush = () => {
    const value = current.trim();
    if (value) chunks.push(value);
    current = "";
  };

  const append = (value) => {
    const part = String(value || "").trim();
    if (!part) return;
    const candidate = current ? `${current} ${part}` : part;
    if (candidate.length <= limit) {
      current = candidate;
      return;
    }
    flush();

    if (part.length <= limit) {
      current = part;
      return;
    }

    // Very long sentences are split on words. This keeps Safari utterances
    // short enough to finish reliably without changing the spoken wording.
    for (const word of part.split(/\s+/)) {
      const next = current ? `${current} ${word}` : word;
      if (current && next.length > limit) flush();
      current = current ? `${current} ${word}` : word;
    }
  };

  sentences.forEach(append);
  flush();
  return chunks;
}

function speechWatchdogMs(chunk, requested) {
  if (Number.isFinite(requested) && requested > 0) {
    return Math.max(15000, requested);
  }
  const words = String(chunk || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(30000, Math.min(90000, 15000 + (words * 1000)));
}

export function speakPrivateMail(text, { signal, timeoutMs } = {}) {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.speechSynthesis ||
        typeof window.SpeechSynthesisUtterance === "undefined" || signal?.aborted) {
      resolve(false);
      return;
    }

    const speech = window.speechSynthesis;
    const chunks = privateMailSpeechChunks(text);
    if (!chunks.length) {
      resolve(true);
      return;
    }

    const voices = speech.getVoices?.() || [];
    const voice = voices.find((item) => item.lang === "en-GB" && /female|serena|kate|martha/i.test(item.name)) ||
      voices.find((item) => item.lang === "en-GB") || null;

    let finished = false;
    let index = 0;
    let watchdog = null;
    let advanceTimer = null;
    let activeUtterance = null;

    const clearTimers = () => {
      if (watchdog) clearTimeout(watchdog);
      if (advanceTimer) clearTimeout(advanceTimer);
      watchdog = null;
      advanceTimer = null;
    };

    const finish = (spoken) => {
      if (finished) return;
      finished = true;
      clearTimers();
      signal?.removeEventListener("abort", cancel);
      resolve(spoken);
    };

    const cancel = () => {
      if (finished) return;
      speech.cancel();
      finish(false);
    };

    const speakNext = () => {
      if (finished) return;
      if (signal?.aborted) {
        cancel();
        return;
      }
      if (index >= chunks.length) {
        finish(true);
        return;
      }

      const chunk = chunks[index];
      index += 1;
      const utterance = new window.SpeechSynthesisUtterance(chunk);
      activeUtterance = utterance;
      utterance.lang = "en-GB";
      utterance.rate = 0.96;
      utterance.voice = voice;

      const advance = () => {
        if (finished || activeUtterance !== utterance) return;
        if (watchdog) clearTimeout(watchdog);
        watchdog = null;
        activeUtterance = null;
        // A tiny hand-off gap avoids Safari swallowing the start of the next
        // sentence while still sounding like one continuous answer.
        advanceTimer = setTimeout(speakNext, 40);
      };

      utterance.onend = advance;
      utterance.onerror = () => finish(false);

      const watch = () => {
        if (finished || activeUtterance !== utterance) return;
        // A watchdog is allowed to recover a missing onend event, but it must
        // NEVER cancel speech that the browser says is still playing/pending.
        if (speech.speaking || speech.pending) {
          watchdog = setTimeout(watch, speechWatchdogMs(chunk, timeoutMs));
          return;
        }
        advance();
      };

      watchdog = setTimeout(watch, speechWatchdogMs(chunk, timeoutMs));
      try {
        speech.speak(utterance);
      } catch {
        finish(false);
      }
    };

    signal?.addEventListener("abort", cancel, { once: true });
    try {
      // Clear only stale browser speech before this answer starts. Once the
      // answer begins, task/transport completion is not allowed to cut it off.
      speech.cancel();
      speakNext();
    } catch {
      finish(false);
    }
  });
}
